import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Tabs, Tab, Paper, Alert } from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  History as HistoryIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import TriviaLeaderboard from "./TriviaLeaderboard";
import TriviaAchievements from "./TriviaAchievements";
import TriviaGameHistory from "./TriviaGameHistory";
import { buildTriviaLeaderboardRows } from "../../utils/triviaLeaderboardRows";
import {
  NAME_SORT_ORDER,
  STAT_SORT_ORDER,
  formatGridStatValue,
  getResultsGridSx,
  sortedClassFor,
} from "../../utils/resultsGrid";

const DailyTriviaTable = ({ rankings, loading }) => {
  const [sortModel, setSortModel] = useState([{ field: "score", sort: "desc" }]);
  const sortedField = sortModel[0]?.field;
  const rows = useMemo(
    () => buildTriviaLeaderboardRows(rankings || [], "daily"),
    [rankings]
  );
  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "#",
        width: 56,
        sortable: false,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "username",
        headerName: "Player",
        flex: 1.2,
        minWidth: 160,
        headerClassName: sortedClassFor("username", sortedField),
        cellClassName: sortedClassFor("username", sortedField),
        sortingOrder: NAME_SORT_ORDER,
      },
      {
        field: "score",
        headerName: "Score",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("score", sortedField),
        cellClassName: sortedClassFor("score", sortedField),
        sortingOrder: STAT_SORT_ORDER,
      },
      {
        field: "accuracy",
        headerName: "Accuracy",
        width: 120,
        type: "number",
        align: "right",
        headerAlign: "right",
        headerClassName: sortedClassFor("accuracy", sortedField),
        cellClassName: sortedClassFor("accuracy", sortedField),
        sortingOrder: STAT_SORT_ORDER,
        renderCell: (params) => formatGridStatValue(params.value, "percent"),
      },
    ],
    [sortedField]
  );

  if (!loading && !rankings?.length) {
    return <Alert severity="info">No daily scores yet. Be the first!</Alert>;
  }

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        rowHeight={52}
        sx={getResultsGridSx()}
      />
    </Paper>
  );
};

const tabIndexForName = {
  leaderboards: 0,
  achievements: 1,
  history: 2,
  daily: 3,
};

const tabNameForIndex = ["leaderboards", "achievements", "history", "daily"];

const TriviaCommunityPanel = ({
  initialTab = "leaderboards",
  globalLeaderboard,
  streakLeaderboard,
  speedLeaderboard,
  dailyLeaderboard,
  achievements,
  achievementCatalog,
  gameHistory,
  historyPagination,
  onHistoryPageChange,
  isLoadingLeaderboards,
  isLoadingStats,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState(tabIndexForName[initialTab] || 0);

  useEffect(() => {
    setActiveTab(tabIndexForName[initialTab] || 0);
  }, [initialTab]);

  const handleChange = (_, value) => {
    setActiveTab(value);
    onTabChange?.(tabNameForIndex[value]);
  };

  return (
    <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Tabs value={activeTab} onChange={handleChange} variant="scrollable">
        <Tab icon={<TrophyIcon />} iconPosition="start" label="Leaderboards" />
        <Tab icon={<TrophyIcon />} iconPosition="start" label="Achievements" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="History" />
        <Tab icon={<TodayIcon />} iconPosition="start" label="Daily" />
      </Tabs>

      <Box sx={{ p: 3 }}>
        {activeTab === 0 && (
          <TriviaLeaderboard
            globalLeaderboard={globalLeaderboard}
            streakLeaderboard={streakLeaderboard}
            speedLeaderboard={speedLeaderboard}
            isLoading={isLoadingLeaderboards}
          />
        )}
        {activeTab === 1 && (
          <TriviaAchievements
            achievements={achievements}
            catalog={achievementCatalog}
            isLoading={isLoadingStats}
          />
        )}
        {activeTab === 2 && (
          <TriviaGameHistory
            gameHistory={gameHistory}
            isLoading={isLoadingStats}
            compact
            pagination={historyPagination}
            onPageChange={onHistoryPageChange}
          />
        )}
        {activeTab === 3 && (
          <DailyTriviaTable
            rankings={dailyLeaderboard?.rankings}
            loading={isLoadingLeaderboards}
          />
        )}
      </Box>
    </Paper>
  );
};

export default TriviaCommunityPanel;
